import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { requireAuthentication } from '../utils/authUtils';
import { getLetterToHeavenList } from '../api';
import '../styles/LetterToHeavenBoardListPC.css';
import '../styles/MemorialAlbumBoardListPC.css';
import FamilyPlazaTopSection from './family-plaza/FamilyPlazaTopSection';
import FamilyPlazaSubHeading from './family-plaza/FamilyPlazaSubHeading';

const LETTERS_PER_PAGE = 9;

const LetterToHeavenBoardListPC = () => {
  const navigate = useNavigate();

  // -------------------- State --------------------
  const [letters, setLetters] = useState([]);
  const [filteredLetters, setFilteredLetters] = useState([]);
  const [allLetters, setAllLetters] = useState([]);
  const [isAllLettersLoaded, setIsAllLettersLoaded] = useState(false);
  const [isPrefetchingAll, setIsPrefetchingAll] = useState(false);

  const [loading, setLoading] = useState(false); // initially false
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreLetters, setHasMoreLetters] = useState(true);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('전체');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);

  const latestRequestRef = useRef(0);
  const initialLoadDoneRef = useRef(false); // prevent multiple initial calls
  const initialLoadRef = useRef(true); // track first render
  const debounceTimerRef = useRef(null);
  const [loadingMore, setLoadingMore] = useState(false); 

  // -------------------- Navigation --------------------
  const handleNavigation = (route) => {
    if (!requireAuthentication()) return;
    navigate(route);
  };

  // -------------------- Filter mapping --------------------
  const mapFilterToSearchType = (filter) => {
    if (!filter) return undefined;
    const f = String(filter).toLowerCase().trim();
    switch (f) {
      case '제목':
      case 'title':
        return 'title';
      case '내용':
      case 'content':
        return 'content';
      case '받으시는 분':
      case '받으시는분':
      case 'to':
      case 'to name':
      case 'to_name':
      case 'to-name':
      case 'toname':
        return 'toName';
      case '보내시는 분':
      case '보내시는분':
      case 'sender':
      case 'from':
      case 'user':
      case 'username':
      case 'user_name':
      case 'user-name':
        return 'userName';
      case '전체':
      case 'all':
        return 'all';
      default:
        return 'all';
    }
  };

  // -------------------- Normalize API data --------------------
  const normalizeItem = (item) => {
    const localData = localStorage.getItem('letterToHeavenDataPC');
    const unifiedData = localStorage.getItem('letterToHeavenData');
    let localRecipient = '';
    let localContent = '';

    if (localData || unifiedData) {
      try {
        const dataToCheck = unifiedData ? JSON.parse(unifiedData) : JSON.parse(localData);
        const lettersArray = Array.isArray(dataToCheck) ? dataToCheck : [dataToCheck];
        const matchingLetter = lettersArray.find(l => l.id == item.id);
        if (matchingLetter) {
          localRecipient = matchingLetter.recipient || '';
          localContent = matchingLetter.content || '';
        }
      } catch (e) { }
    }

    return {
      id: item.id,
      to: item.toName || item.recipient || item.to || item.to_name || item.recipientName || item.recipient_name || localRecipient || '',
      title: item.title,
      sender: item.userName || item.sender || '',
      content: item.content || localContent || '',
      date: item.registDate || item.date,
      selectedStamp: (typeof item.boardStamp !== 'undefined') ? item.boardStamp : (item.selectedStamp || item.flowerImageUrl)
    };
  };

  // -------------------- Fetch Page --------------------
  const fetchPage = useCallback(async ({ page, append = false, showLoading = true }) => {
    const requestId = ++latestRequestRef.current;
    try {
      if (showLoading) setLoading(true); // only show loading when needed
      const response = await getLetterToHeavenList({ page, size: LETTERS_PER_PAGE });
      if (requestId !== latestRequestRef.current) return; // stale

      const newLetters = response.data?.content || [];
      const normalized = newLetters.map(normalizeItem);

      if (append) {
        setLetters(prev => [...prev, ...normalized]);
      } else {
        setLetters(normalized);
        if (!isSearchActive) setFilteredLetters(normalized);
      }

      setHasMoreLetters(newLetters.length === LETTERS_PER_PAGE);
      setError(null);
    } catch (err) {
      console.error('❌ BOARD LIST FETCH ERROR:', err);
      if (requestId === latestRequestRef.current) setError(err.message || 'Error fetching letters');
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [isSearchActive]);

  // -------------------- Fetch ALL Letters Once --------------------
  const fetchAllLettersOnce = useCallback(async () => {
    if (isAllLettersLoaded || isPrefetchingAll) return;
    setIsPrefetchingAll(true);
    try {
      const first = await getLetterToHeavenList({ page: 1, size: LETTERS_PER_PAGE });
      const pageData = first.data?.content ?? [];
      const totalPages = first.data?.totalPages ?? 1;

      const collected = [...(pageData || [])];

      if (totalPages > 1) {
        for (let p = 2; p <= totalPages; p++) {
          try {
            const res = await getLetterToHeavenList({ page: p, size: LETTERS_PER_PAGE });
            collected.push(...(res.data?.content ?? []));
          } catch (innerErr) {
            console.warn(`Warning: failed to fetch page ${p} while prefetching all letters`, innerErr);
          }
        }
      }

      const normalizedAll = collected.map(normalizeItem);
      setAllLetters(normalizedAll);
      setIsAllLettersLoaded(true);
    } catch (err) {
      console.error('❌ fetchAllLettersOnce failed:', err);
    } finally {
      setIsPrefetchingAll(false);
    }
  }, [isAllLettersLoaded, isPrefetchingAll]);

  // -------------------- Client-side Filtering --------------------
  const filterLettersClientSide = (lettersArr, query, filterKey) => {
    if (!query || !query.trim()) return lettersArr;
    const term = query.toLowerCase().trim();

    switch (filterKey) {
      case 'title':
        return lettersArr.filter(l => l.title?.toLowerCase().includes(term));
      case 'content':
        return lettersArr.filter(l => l.content?.toLowerCase().includes(term));
      case 'toName':
        return lettersArr.filter(l => l.to?.toLowerCase().includes(term));
      case 'userName':
        return lettersArr.filter(l => l.sender?.toLowerCase().includes(term));
      case 'all':
      default:
        return lettersArr.filter(l =>
          l.title?.toLowerCase().includes(term) ||
          l.content?.toLowerCase().includes(term) ||
          l.sender?.toLowerCase().includes(term) ||
          l.to?.toLowerCase().includes(term)
        );
    }
  };

  // -------------------- Handle Search --------------------
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setIsSearchActive(false);
      setFilteredLetters(letters);
      return;
    }

    setIsSearchActive(true);
    setLoading(true); 

    if (isAllLettersLoaded) {
      const filterKey = mapFilterToSearchType(selectedFilter);
      const results = filterLettersClientSide(allLetters, searchQuery, filterKey);
      setFilteredLetters(results);
      setHasMoreLetters(false);
      setLoading(false);
      return;
    }

    try {
      await fetchAllLettersOnce();
      const filterKey = mapFilterToSearchType(selectedFilter);
      const results = filterLettersClientSide(allLetters.length ? allLetters : [], searchQuery, filterKey);
      setFilteredLetters(results);
      setHasMoreLetters(false);
    } catch (err) {
      console.error('❌ handleSearch fallback failed:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedFilter, allLetters, isAllLettersLoaded, letters, fetchAllLettersOnce]);

  // -------------------- Input Handlers --------------------
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!value.trim()) {
      setIsSearchActive(false);
      setFilteredLetters(letters);
    }
  };

  const handleFilterChange = async (filter) => {
    setSelectedFilter(filter);
    setIsDropdownOpen(false);

    if (isSearchActive) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        if (isAllLettersLoaded) {
          const results = filterLettersClientSide(allLetters, searchQuery, mapFilterToSearchType(filter));
          setFilteredLetters(results);
        } else {
          handleSearch();
        }
      }, 200); 
    }
  };

  // const handleLoadMore = () => {
  //   if (isSearchActive) return;
  //   const next = currentPage + 1;
  //   setCurrentPage(next);
  //   fetchPage({ page: next, append: true });
  // };
  

  const handleLoadMore = async () => {
    if (loadingMore) return; // prevent multiple clicks
    setLoadingMore(true);

    const next = currentPage + 1;
    setCurrentPage(next);

    try {
      await fetchPage({ page: next, append: true });
    } finally {
      setLoadingMore(false);
    }
  };



  const handleLetterClick = (letterId) => {
    const currentLetters = isSearchActive ? filteredLetters : letters;
    const letterData = currentLetters.find(letter => letter.id === letterId);
    if (letterData) {
      localStorage.setItem('selectedLetterRecipient', letterData.to || '');
    }
    navigate(`/letter-to-heaven-post-details/${letterId}`);
  };

  // -------------------- Stamp & Date Helpers --------------------
  const getStampImage = (selectedStamp) => {
    const staticImages = {
      1: '/heaven/getty-images-145244798.png',
      2: '/heaven/getty-images-465710299.png',
      3: '/heaven/getty-images-955651052.png',
      4: '/heaven/getty-images-907936742.png',
      5: '/heaven/getty-images-494124656.png',
      6: '/heaven/getty-images-926601494.png',
      7: '/heaven/getty-images-537807854.png'
    };
    if (typeof selectedStamp === 'string' && selectedStamp.startsWith('http')) return selectedStamp;
    if (typeof selectedStamp === 'number') return staticImages[selectedStamp] || staticImages[1];
    if (typeof selectedStamp === 'string' && selectedStamp.startsWith('stamp-')) {
      const num = parseInt(selectedStamp.replace('stamp-', ''), 10);
      return staticImages[num] || staticImages[1];
    }
    return staticImages[1];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      if (/^\d{4}\.\d{2}\.\d{2}$/.test(dateString)) return dateString;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString.replace(/-/g, '.');
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch (error) {
      return dateString;
    }
  };

  // -------------------- Initial Load --------------------
  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      fetchPage({ page: 1, append: false, showLoading: false }); // initial load without loading
      fetchAllLettersOnce();
      initialLoadRef.current = false;
    }
  }, [fetchPage, fetchAllLettersOnce]);

  // Refresh when window focus
  useEffect(() => {
    const handleFocus = () => {
      fetchPage({ page: 1, append: false, showLoading: false });
      fetchAllLettersOnce();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchPage, fetchAllLettersOnce]);

  // -------------------- Manual Search Only (button/enter) --------------------
  useEffect(() => {
    if (!searchQuery.trim()) {
      setIsSearchActive(false);
      setFilteredLetters(letters);
    }
  }, [searchQuery, letters]);

  // -------------------- Render --------------------
  const lettersToDisplay = isSearchActive ? filteredLetters : letters;

  return (
    <div className="letter-to-heaven-board-list-pc page-letter-to-heaven-board-list-pc announcement-list-page donate-info-page-pc">
      <FamilyPlazaTopSection />
      <main className="main-content">
        <FamilyPlazaSubHeading heading="하늘로 보내는 편지" />
        <div className="letter-to-heaven-board-list-main-content">
          <div className="letter-to-heaven-board-list-page-content">
            <div className="letter-to-heaven-board-list-container">
              <div className="letter-to-heaven-board-list-content-area">

                {/* Search Bar */}
                <div className="letter-to-heaven-board-list-search-section">
                  <div className="letter-to-heaven-board-list-search-form">
                    <div className="letter-to-heaven-board-list-search-row">

                      <div className="letter-to-heaven-board-list-dropdown">
                        <div className="letter-to-heaven-board-list-dropdown-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                          <span className="letter-to-heaven-board-list-dropdown-selected">{selectedFilter}</span>
                          <svg className={`letter-to-heaven-board-list-dropdown-caret ${isDropdownOpen ? 'rotated' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 4L6 7L9 4" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        {isDropdownOpen && (
                          <div className="letter-to-heaven-board-list-dropdown-menu">
                            {['전체', '제목', '내용', '받으시는 분', '보내시는 분'].map(item => (
                              <div key={item} className={`letter-to-heaven-board-list-dropdown-item ${selectedFilter === item ? 'active' : ''}`} onClick={() => handleFilterChange(item)}>
                                {item}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="letter-to-heaven-board-list-search-input">
                        <input
                          type="text"
                          placeholder="Search"
                          value={searchQuery}
                          onChange={handleSearchInputChange}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                        />
                      </div>

                      <div className="letter-to-heaven-board-list-search-button">
                        <button type="button" onClick={handleSearch}>검색</button>
                      </div>

                    </div>
                  </div>
                </div>

                {/* Letter Cards Grid */}
                <div className="letter-to-heaven-board-list-cards-grid">
                  {loading ? (
                    <div className="letter-to-heaven-board-list-loading">
                      <p>Loading letters...</p>
                    </div>
                  ) : lettersToDisplay.length > 0 ? (
                    lettersToDisplay.map(letter => (
                      <div key={`dynamic-${letter.id}`} className="letter-to-heaven-board-list-card" onClick={() => handleLetterClick(letter.id)} style={{ cursor: 'pointer' }}>
                        <div className="letter-to-heaven-board-list-card-content">
                          <div className="letter-to-heaven-board-list-card-text">
                            <p className="letter-to-heaven-board-list-card-to">To. {letter.to || '하늘'}</p>
                            <p className="letter-to-heaven-board-list-card-title">{letter.title}</p>
                          </div>
                          <div className="letter-to-heaven-board-list-card-image">
                            <img
                              src={getStampImage(letter.selectedStamp)}
                              alt="Letter to heaven"
                              onError={(e) => { e.target.src = '/heaven/getty-images-145244798.png'; }}
                            />
                          </div>
                        </div>
                        <div className="letter-to-heaven-board-list-card-footer">
                          <p className="letter-to-heaven-board-list-card-date">{formatDate(letter.date)}</p>
                          <p className="letter-to-heaven-board-list-card-from">From. {letter.sender}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="letter-to-heaven-board-list-no-data">
                      <p>{isSearchActive ? '검색 결과가 없습니다.' : 'No letters available'}</p>
                    </div>
                  )}
                </div>

                {/* More Button (hide when searching) */}
                {/* {!isSearchActive && hasMoreLetters && !loading && (
                  <div className="letter-to-heaven-board-list-more-section">
                    <button className="letter-to-heaven-board-list-more-btn" onClick={handleLoadMore}>더보기</button>
                  </div>
                )} */}

               
                {/* More Button (always visible) */}
                <div className="letter-to-heaven-board-list-more-section">
                  <button
                    className="letter-to-heaven-board-list-more-btn"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? '로딩 중...' : '더보기'}
                  </button>
                </div>



                {/* Bottom Action Bar */}
                <div className="letter-to-heaven-board-list-bottom-action">
                  <div className="letter-to-heaven-board-list-checkbox-container">
                    <div className="letter-to-heaven-board-list-checkbox-row">
                      <img src="/images/icon4.png" alt="Check" className="letter-to-heaven-board-list-check-icon" />
                      <span className="letter-to-heaven-board-list-checkbox-label">소중한 사람에게 마음으로 보내는 편지</span>
                    </div>
                    <p className="letter-to-heaven-board-list-description">나의 그립고 소중한 사람에게 마음을 담은 편지를 적어 하늘로 보내세요</p>
                  </div>
                  <button className="letter-to-heaven-board-list-write-btn" onClick={() => navigate('/letter-to-heaven-writing')}>글쓰기</button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LetterToHeavenBoardListPC;


